import React, { useState, useEffect, useMemo } from 'react';
import { Landmark, Upload, Check, ArrowDownToLine, ArrowUpFromLine, FileText } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export default function Bankimport() {
const { t, lang } = useT();
  const { klanten, refreshAll } = useData();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {formaat, transacties:[...]}
  const [keuze, setKeuze] = useState({}); // idx -> "factuur:id" | "kost:id" | ""
  const [openFacturen, setOpenFacturen] = useState([]);
  const [openKosten, setOpenKosten] = useState([]);

  const klantNaam = useMemo(() => {
    const m = {}; (klanten || []).forEach(k => { m[k.id] = k.naam; }); return m;
  }, [klanten]);

  useEffect(() => {
    carsysApi.getBonnen().then(bs => setOpenFacturen((bs || []).filter(b => (b.openstaand || 0) > 0.005))).catch(() => {});
    carsysApi.getKosten().then(ks => setOpenKosten((ks || []).filter(k => !k.betaald))).catch(() => {});
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await carsysApi.parseBankFile(file);
      setResult(res);
      // Standaard de voorgestelde matches selecteren
      const init = {};
      (res.transacties || []).forEach(tx => {
        if (tx.suggestie) init[tx.idx] = `${tx.suggestie.type}:${tx.suggestie.id}`;
      });
      setKeuze(init);
      toast.success(t('Bestand ingelezen', 'Bestand ingelezen'), { description: `${res.aantal} ${t('transacties', 'transacties')} (${res.formaat})` });
    } catch (err) {
      toast.error(t('Inlezen mislukt', 'Inlezen mislukt'), { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const gekoppeld = useMemo(() => Object.values(keuze).filter(Boolean).length, [keuze]);

  const verwerk = async () => {
    const txById = {};
    (result?.transacties || []).forEach(tx => { txById[tx.idx] = tx; });
    const items = Object.entries(keuze).filter(([, v]) => v).map(([idx, v]) => {
      const [matchType, matchId] = v.split(':');
      const tx = txById[idx];
      return { matchType, matchId, bedrag: tx.bedrag, datum: tx.datum, omschrijving: tx.omschrijving };
    });
    if (items.length === 0) { toast.error(t('Selecteer minstens 1 koppeling', 'Selecteer minstens 1 koppeling')); return; }
    setBusy(true);
    try {
      const res = await carsysApi.confirmBankImport(items);
      toast.success(t('Afgeletterd', 'Afgeletterd'), { description: `${res.facturen} ${t('facturen', 'facturen')} + ${res.kosten} ${t('kosten', 'kosten')}` });
      setResult(null); setKeuze({});
      await refreshAll();
      carsysApi.getBonnen().then(bs => setOpenFacturen((bs || []).filter(b => (b.openstaand || 0) > 0.005))).catch(() => {});
      carsysApi.getKosten().then(ks => setOpenKosten((ks || []).filter(k => !k.betaald))).catch(() => {});
    } catch (err) {
      toast.error(t('Verwerken mislukt', 'Verwerken mislukt'), { description: err.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="bankimport-page">
      <div className="cs-page-header">
        <div><h2>{t('Bankimport', 'Bankimport')}</h2><p>{t('Importeer een bankafschrift (MT940 of CAMT.053) en letter automatisch af tegen openstaande facturen en kosten.', 'Importeer een bankafschrift (MT940 of CAMT.053) en letter automatisch af tegen openstaande facturen en kosten.')}</p></div>
        {result && (
          <button data-testid="btn-verwerk-import" className="cs-btn primary" disabled={busy || gekoppeld === 0} onClick={verwerk}>
            <Check size={14} /> {gekoppeld} {t('koppeling(en) verwerken', 'koppeling(en) verwerken')}
          </button>
        )}
      </div>

      {!result && (
        <div className="cs-card padded" style={{ maxWidth: 640 }} data-testid="bankimport-upload-card">
          <h3 className="cs-card-title"><Landmark size={16} /> {t('Afschrift uploaden', 'Afschrift uploaden')}</h3>
          <p style={{ marginTop: 0, fontSize: 13, color: 'var(--cs-text-muted)' }}>
            {t('Exporteer een afschrift uit je bankieromgeving (ING/Rabobank/ABN AMRO) als MT940 (.sta/.txt) of CAMT.053 (.xml) en sleep het hier in.', 'Exporteer een afschrift uit je bankieromgeving (ING/Rabobank/ABN AMRO) als MT940 (.sta/.txt) of CAMT.053 (.xml) en sleep het hier in.')}
          </p>
          <label className="cs-btn primary" style={{ cursor: 'pointer', display: 'inline-flex' }}>
            <Upload size={14} /> {busy ? t('Bezig...', 'Bezig...') : t('Kies bestand', 'Kies bestand')}
            <input data-testid="input-bankfile" type="file" accept=".xml,.sta,.txt,.940,.mt940" onChange={handleFile} style={{ display: 'none' }} disabled={busy} />
          </label>
        </div>
      )}

      {result && (
        <div className="cs-card padded" data-testid="bankimport-result-card">
          <h3 className="cs-card-title"><FileText size={16} /> {result.formaat} — {result.aantal} {t('transacties', 'transacties')}
            <span style={{ fontSize: 11, color: 'var(--cs-text-muted)', fontWeight: 400 }}>— {t('controleer de voorgestelde koppelingen', 'controleer de voorgestelde koppelingen')}</span>
          </h3>
          <table className="cs-table" data-testid="bankimport-table">
            <thead><tr>
              <th>{t('Datum')}</th><th>{t('Omschrijving')}</th><th style={{ textAlign: 'right' }}>{t('Bedrag')}</th><th style={{ width: 300 }}>{t('Koppelen aan', 'Koppelen aan')}</th>
            </tr></thead>
            <tbody>
              {(result.transacties || []).map(tx => {
                const bij = tx.bedrag >= 0;
                const opties = bij ? openFacturen : openKosten;
                return (
                  <tr key={tx.idx} data-testid={`bankimport-row-${tx.idx}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{tx.datum ? new Date(tx.datum).toLocaleDateString(lang === 'en' ? 'en-GB' : 'nl-NL') : '-'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {bij ? <ArrowDownToLine size={13} style={{ color: 'var(--cs-green, #16a34a)' }} /> : <ArrowUpFromLine size={13} style={{ color: '#dc2626' }} />}
                        <span style={{ fontWeight: 600 }}>{tx.tegenpartij || t('Onbekend', 'Onbekend')}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{tx.omschrijving}</div>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: bij ? '#16a34a' : '#dc2626', whiteSpace: 'nowrap' }}>
                      {bij ? '+ ' : '− '}{eur(Math.abs(tx.bedrag))}
                    </td>
                    <td>
                      <select
                        className="cs-input"
                        style={{ padding: 5 }}
                        data-testid={`bankimport-select-${tx.idx}`}
                        value={keuze[tx.idx] || ''}
                        onChange={e => setKeuze(k => ({ ...k, [tx.idx]: e.target.value }))}
                      >
                        <option value="">— {t('niet koppelen', 'niet koppelen')} —</option>
                        {bij
                          ? opties.map(f => <option key={f.id} value={`factuur:${f.id}`}>{f.nummer} — {klantNaam[f.klantId] || ''} — {eur(f.openstaand)}</option>)
                          : opties.map(k => <option key={k.id} value={`kost:${k.id}`}>{(k.factuurnummer || k.leverancier || t('kost','kost'))} — {k.leverancier || ''} — {eur(k.bedragIncl)}</option>)
                        }
                      </select>
                      {tx.suggestie && (
                        <div style={{ fontSize: 10.5, color: 'var(--cs-text-muted)', marginTop: 3 }}>
                          {t('Voorstel', 'Voorstel')}: <b>{tx.suggestie.label}</b> <span className="cs-badge gray" style={{ fontSize: 9 }}>{tx.suggestie.reden === 'factuurnummer' ? t('o.b.v. nummer', 'o.b.v. nummer') : t('o.b.v. bedrag', 'o.b.v. bedrag')}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button className="cs-btn ghost" onClick={() => { setResult(null); setKeuze({}); }}>{t('Annuleren')}</button>
            <button data-testid="btn-verwerk-import-foot" className="cs-btn primary" disabled={busy || gekoppeld === 0} onClick={verwerk}>
              <Check size={14} /> {gekoppeld} {t('koppeling(en) verwerken', 'koppeling(en) verwerken')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
